import PickupType from "../enums/PickupType";
import Scenes from "../enums/Scenes";
import WeaponType from "../enums/WeaponType";
import Game from "../game/Game";
import LevelUpItemPanel from "./LevelUpItemPanel";
import { Sizer } from "phaser3-rex-plugins/templates/ui/ui-components";
import { TEXT_STYLES } from "../../utils/FontManager";
import { t } from "../../utils/i18n/index";
import { DEFAULT_SCALE } from "../consts/UI";
import { getCurrentPerformanceLevelSync } from "../../utils/performance";

export default class LevelUpScene extends Phaser.Scene {
  CurrentAmountOfPanels: number;
  panels: LevelUpItemPanel[];
  background: Phaser.GameObjects.Graphics;
  mask: any;
  headerText: Phaser.GameObjects.Text;
  footerText: Phaser.GameObjects.Text;
  sizer: Sizer;
  private scaleRatio: number;
  private confettiEmitter: Phaser.GameObjects.Container | null;
  private confettiColors: number[];
  private subtitleText: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    super({ key: Scenes.UI_levelUpScene });
    this.panels = new Array();
    this.CurrentAmountOfPanels = 3;
    this.confettiColors = [
      0xffd700, 0xff4500, 0x00bfff, 0x32cd32, 0xff69b4, 0xba55d3, 0xffa500,
    ];
    this.confettiEmitter = null;
  }
  create() {
    // Play level up sound once when scene is created using pooled audio
    Game.Core.poolManager?.playLevelUpMusic();

    this.scaleRatio = DEFAULT_SCALE;

    // Use a consistent depth approach like TreasureScene
    const BASE_DEPTH = 1000;

    // Layer 1: Background mask
    this.mask = this.add.graphics().setScrollFactor(0);
    this.mask.fillStyle(0x000000, 0.8);
    this.mask.fillRect(0, 0, this.cameras.main.width, this.cameras.main.height);
    this.mask.setDepth(BASE_DEPTH);

    // Layer 2: Add confetti particles behind UI elements (only on medium/high performance devices)
    const performanceLevel = getCurrentPerformanceLevelSync();
    if (performanceLevel !== "low") {
      this.createConfetti(BASE_DEPTH + 1);
    }

    // Layer 3: UI Elements in a container for proper depth management
    const uiContainer = this.add.container(0, 0).setDepth(BASE_DEPTH + 10);

    // Fixed size for the dialog box: 345x377 px (scaled)
    const dialogWidth = 345 * this.scaleRatio;
    const dialogHeight = 377 * this.scaleRatio;

    this.sizer = this.rexUI.add.sizer({
      width: dialogWidth,
      height: dialogHeight,
      anchor: {
        centerX: "center",
        centerY: "center",
      },
      space: {
        left: 20 * this.scaleRatio,
        right: 20 * this.scaleRatio,
        top: 40 * this.scaleRatio,
        bottom: 20 * this.scaleRatio,
        item: 10 * this.scaleRatio,
      },
      orientation: 1,
    });

    // Dark background with rounded corners
    this.sizer.addBackground(
      this.rexUI.add.roundRectangle(0, 0, 0, 0, 20 * this.scaleRatio, 0x1c1c1e)
    );

    // Pixel font header
    this.headerText = this.add.text(0, 0, t("game.levelUp"), {
      ...TEXT_STYLES.PIXEL,
      fontSize: `${22 * this.scaleRatio}px`,
      color: "#FFFFFF",
      wordWrap: { width: dialogWidth - 40 * this.scaleRatio },
    });

    // Add subtitle text
    this.subtitleText = this.add.text(0, 0, t("game.selectUpgrade"), {
      ...TEXT_STYLES.REGULAR,
      fontSize: `${16 * this.scaleRatio}px`,
      color: "#84919E",
      wordWrap: { width: dialogWidth - 40 * this.scaleRatio },
    });

    // Add both text elements to sizer
    this.sizer.add(this.headerText, {
      padding: { bottom: 5 * this.scaleRatio },
    });

    this.sizer.add(this.subtitleText, {
      padding: { bottom: 20 * this.scaleRatio },
    });

    // Create panels with fixed size: 306x70 px (scaled)
    const panelWidth = 306 * this.scaleRatio;

    for (let i = 0; i < 4; i++) {
      this.panels[i] = new LevelUpItemPanel(
        this,
        panelWidth,
        70 * this.scaleRatio,
        this.scaleRatio
      );
      this.sizer.add(this.panels[i], {});
    }

    // Footer text
    this.footerText = this.add.text(0, 0, t("game.moreLuckBringsMoreOptions"), {
      ...TEXT_STYLES.REGULAR,
      fontSize: `${12 * this.scaleRatio}px`,
      color: "#84919E",
    });

    this.sizer.add(this.footerText);

    if (Game.Core.LevelUpFactory.hasPowerupsInStore()) {
      this.PickRandomLevelUps();
    } else {
      this.PickItemLevelUps();
    }

    this.sizer.layout();
    uiContainer.add(this.sizer);
  }
  createConfetti(depth: number) {
    // We'll create individual confetti pieces as graphics objects
    const confettiContainer = this.add.container(0, 0);
    confettiContainer.setDepth(depth);

    // Create 500 confetti pieces
    for (let i = 0; i < 500; i++) {
      const confetti = this.add.graphics();
      const color = Phaser.Utils.Array.GetRandom(this.confettiColors);
      confetti.fillStyle(color, 1);

      // Random shape (circle or rectangle)
      const shape = Math.random() > 0.5 ? "circle" : "rect";
      const size = Phaser.Math.Between(5, 15) * this.scaleRatio;

      if (shape === "circle") {
        confetti.fillCircle(0, 0, size / 2);
      } else {
        confetti.fillRect(-size / 2, -size / 2, size, size);
      }

      // Start from center-bottom of screen (explosion source)
      const centerX = this.cameras.main.width / 2;
      const startY = this.cameras.main.height * 0.6;
      confetti.x = centerX + Phaser.Math.Between(-20, 20);
      confetti.y = startY + Phaser.Math.Between(-10, 10);

      // Explosive animation sequence
      const angle = Phaser.Math.Between(-170, -10); // Mostly upward
      const distance =
        Phaser.Math.Between(100, this.cameras.main.height * 0.8) *
        this.scaleRatio;
      const duration1 = Phaser.Math.Between(700, 1500);

      // First tween: explosion upward
      this.tweens.add({
        targets: confetti,
        x: confetti.x + distance * Math.cos((angle * Math.PI) / 180),
        y: confetti.y + distance * Math.sin((angle * Math.PI) / 180),
        rotation: Phaser.Math.Between(-2, 2),
        duration: duration1,
        ease: "Cubic.easeOut",
        onComplete: () => {
          // Second tween: fall down with gravity
          this.tweens.add({
            targets: confetti,
            y: this.cameras.main.height + 50,
            x: confetti.x + Phaser.Math.Between(-100, 100),
            rotation: confetti.rotation + Phaser.Math.Between(-4, 4),
            duration: Phaser.Math.Between(1500, 3000),
            ease: "Cubic.easeIn",
            delay: Phaser.Math.Between(0, 500), // Staggered falling
          });
        },
      });

      confettiContainer.add(confetti);
    }

    this.confettiEmitter = confettiContainer;
  }
  cleanupConfetti() {
    // Clean up the confetti emitter
    if (this.confettiEmitter) {
      this.confettiEmitter.destroy();
      this.confettiEmitter = null;
    }
  }
  PickItemLevelUps() {
    const levelUpItems = Game.Core.LevelUpFactory.getLevelUpItems();
    this.CurrentAmountOfPanels = levelUpItems.length;
    this.panels.forEach((panel) => {
      const item = levelUpItems.pop();
      if (item) {
        panel.visible = true;
        panel.AssignItemData(item);
      } else {
        panel.visible = false;
      }
    });
    this.enableItemPanelsInput();
  }
  enableItemPanelsInput() {
    this.panels.forEach((panel) => {
      this.rexUI.add.click(panel).once("click", () => {
        this.OnItemButtonClicked(panel.weaponType as PickupType);
      });
    });
  }
  OnItemButtonClicked(weaponType: PickupType) {
    Game.Core.MakeAndActivatePickup(weaponType);
    Game.Core.SceneManager.ResumeFromLevelUp();
  }
  PickRandomLevelUps() {
    const levelUpItems = Game.Core.LevelUpFactory.getLevelUpPowerups();
    this.CurrentAmountOfPanels = levelUpItems.length;
    this.panels.forEach((panel) => {
      const item = levelUpItems.pop();
      if (item) {
        panel.visible = true;
        panel.AssignData(item, Game.Core.GetWeaponLevel(item));
      } else {
        panel.visible = false;
      }
    });
    this.enablePanelsInput();
  }

  enablePanelsInput() {
    this.panels.forEach((panel) => {
      this.rexUI.add.click(panel).once("click", () => {
        this.OnButtonClicked(panel.weaponType as WeaponType);
      });
    });
  }

  OnButtonClicked(weaponType: WeaponType) {
    Game.Core.LevelWeaponUp(weaponType);
    Game.Core.SceneManager.ResumeFromLevelUp();
  }
}
